import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { getComicById, getRelatedComics } from '@/mocks/comics';
import { categoryMap } from '@/mocks/categories';

export default function ComicDetail() {
  const { id } = useParams<{ id: string }>();
  const comic = id ? getComicById(id) : undefined;

  if (!comic) {
    return (
      <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-20 text-center">
        <i className="ri-emotion-sad-line text-6xl text-foreground-300 mb-4 block"></i>
        <h1 className="text-2xl font-heading text-foreground-900 mb-2">漫画未找到</h1>
        <p className="text-foreground-500 mb-6">您查找的漫画不存在或已被移除</p>
        <Link
          to="/"
          className="inline-block px-6 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const relatedComics = getRelatedComics(comic, 4);
  const category = categoryMap[comic.categorySlug];

  const comicSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 色漫屋在线漫画`,
    description: comic.description,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    datePublished: comic.chapters[0]?.updatedAt || comic.updatedAt,
    dateModified: comic.updatedAt,
    image: comic.coverImage,
    publisher: {
      '@type': 'Organization',
      name: '色漫屋',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://baixinsh.com.cn/comic/${comic.id}`,
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: `${comic.title}是什么类型的漫画？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `${comic.title}是一部${comic.category}类漫画作品，由${comic.author}创作，故事${comic.description.substring(0, 60)}...`,
        },
      },
      {
        '@type': 'Question',
        name: `${comic.title}在哪里可以免费阅读？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `${comic.title}可以在色漫屋（baixinsh.com.cn）免费在线阅读，色漫屋提供海量漫画资源，更新快速，分类清晰。`,
        },
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${comic.title} - ${comic.author} | 色漫屋在线漫画免费阅读`}
        description={`${comic.title}，${comic.author}作品，${comic.category}漫画。${comic.description.substring(0, 100)} 在色漫屋免费在线阅读${comic.title}最新章节。`}
        keywords={`${comic.title},${comic.title}在线,${comic.title}免费,色漫屋${comic.category}漫画,${comic.author}`}
        canonicalUrl={`https://baixinsh.com.cn/comic/${comic.id}`}
        ogType="article"
        ogTitle={`${comic.title} - 色漫屋在线漫画`}
        ogDescription={comic.description.substring(0, 150)}
        ogImage={comic.coverImage}
        structuredData={{ ...comicSchema, ...faqSchema, '@context': 'https://schema.org' }}
      />

      <article>
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-8">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              ...(category ? [{ label: `${category.name}漫画`, href: `/category/${comic.categorySlug}` }] : []),
              { label: comic.title },
            ]}
          />

          <div className="mt-8 flex flex-col md:flex-row gap-8">
            <div className="flex-shrink-0">
              <div className="w-48 md:w-56 aspect-[3/4] rounded-xl overflow-hidden bg-background-200">
                <img
                  src={comic.coverImage}
                  alt={`${comic.title} - 色漫屋漫画封面`}
                  title={`${comic.title} - 色漫屋在线漫画`}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="text-2xl md:text-3xl font-heading text-foreground-900">
                {comic.title}
              </h1>
              <p className="text-sm text-foreground-500 mt-2">
                作者：<strong>{comic.author}</strong>
              </p>

              <div className="flex flex-wrap items-center gap-2 mt-4">
                <span className={`px-3 py-1 rounded-md text-xs font-medium text-background-50 whitespace-nowrap ${comic.status === '连载中' ? 'bg-primary-500' : 'bg-foreground-600'}`}>
                  {comic.status}
                </span>
                <span className="px-3 py-1 bg-secondary-100 text-secondary-700 rounded-md text-xs font-medium whitespace-nowrap">
                  {comic.category}
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-4 mt-4">
                <div className="flex items-center gap-1 text-accent-500">
                  <i className="ri-star-fill w-5 h-5 flex items-center justify-center"></i>
                  <span className="text-lg font-bold">{comic.rating}</span>
                </div>
                <div className="flex items-center gap-1 text-foreground-600 text-sm">
                  <i className="ri-eye-line w-5 h-5 flex items-center justify-center"></i>
                  <span>{comic.views >= 10000 ? `${(comic.views / 10000).toFixed(1)}万` : comic.views} 次阅读</span>
                </div>
                <div className="flex items-center gap-1 text-foreground-600 text-sm">
                  <i className="ri-heart-line w-5 h-5 flex items-center justify-center"></i>
                  <span>{comic.favorites >= 10000 ? `${(comic.favorites / 10000).toFixed(1)}万` : comic.favorites} 收藏</span>
                </div>
              </div>

              <div className="mt-6">
                <h2 className="text-base font-semibold text-foreground-900 mb-2">作品简介</h2>
                <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
              </div>

              <div className="flex flex-wrap gap-2 mt-4">
                {comic.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-background-100 text-foreground-600 rounded-full text-xs whitespace-nowrap"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-12">
            <h2 className="text-xl font-heading text-foreground-900 mb-6 flex items-center gap-2">
              <i className="ri-book-open-line text-primary-500"></i>
              章节列表
              <span className="text-sm font-normal text-foreground-500">（共 {comic.chapters.length} 话）</span>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {comic.chapters.map((chapter) => (
                <div
                  key={chapter.id}
                  className="flex items-center justify-between p-4 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-200 hover:bg-primary-50/30 transition-all duration-200 cursor-pointer group"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-full bg-background-200 text-foreground-600 text-xs font-medium">
                      {chapter.number}
                    </span>
                    <div className="min-w-0">
                      <h3 className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors duration-200 line-clamp-1">
                        {chapter.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-0.5">{chapter.updatedAt}</p>
                    </div>
                  </div>
                  <i className="ri-arrow-right-s-line text-foreground-400 group-hover:text-primary-500 transition-colors flex-shrink-0 w-5 h-5 flex items-center justify-center"></i>
                </div>
              ))}
            </div>
          </div>

          {relatedComics.length > 0 && (
            <div className="mt-14">
              <h2 className="text-xl font-heading text-foreground-900 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {relatedComics.map((related) => (
                  <ComicCard key={related.id} comic={related} />
                ))}
              </div>
            </div>
          )}
        </div>
      </article>
    </>
  );
}